import React from "react";
//#region types
import { NestedTableRowProps } from "models/propTypes/simpleTableProps";
import { IconDefinitionConstants } from "utils/constants";
import { TableEnums } from "models/enums";
//#endregion
//#region components
import { Grid, TableCell, TableRow } from "@mui/material";
import CheckBox from "../../forms/baseFields/checkBox";
import Icon from "../../icon";
import useValueFormatter from "../useValueFormatter";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";

const NestedTableRow: React.FC<NestedTableRowProps> = (props) => {
  const {
    row,
    index,
    parentIndex,
    path,
    childrenKey,
    hasChildren: hasChildRows,
    depth,
    repeatProperties,
    columns,
    repeatColumnLength,
    hasMultiLevels,
    allowCheckBoxSelection,
    toggleExpansionHandler,
    toggleSelectionHandler,
    rowFooterRenderer,
  } = props;

  const repeatPropertiesLength = repeatProperties?.properties ? Object.entries(repeatProperties.properties).length : 0;

  const repeatPropertiesList =
    repeatProperties &&
    Object.entries(repeatProperties.properties).filter(
      ([repeatKey, value]) => !props.hiddenColumns?.some((hc) => hc === repeatKey)
    );

  const isImmediateChildHasDisplayType =
    row[childrenKey]?.length && row.expanded && props.rowDisplayType && props.rowDisplayType(row[childrenKey][0]);

  const hasChildren = hasChildRows ? hasChildRows(row) : row[childrenKey]?.length;

  const onClickRow = (row: any, rowIndex: number, parentIndex: number) => {
    if (hasMultiLevels || (!hasMultiLevels && depth === 0)) {
      toggleExpansionHandler && toggleExpansionHandler(rowIndex, parentIndex, path);
    }
    props.rowClickHandler && props.rowClickHandler(row, rowIndex, depth);
  };

  const { getFormattedValue } = useValueFormatter();
  const rowDisplayType = props.rowDisplayType ? props.rowDisplayType(row) : undefined;

  const getRowDisplayTypeClass = () => {
    switch (rowDisplayType) {
      case TableEnums.RowDisplayType.SumSmall:
        return cssExports.sumSmall;
      case TableEnums.RowDisplayType.Sum:
        return cssExports.sum;
      default:
        return "";
    }
  };

  const getColumnSumNegative = (repeatColumnIndex: number) => {
    return (
      rowDisplayType === TableEnums.RowDisplayType.Sum &&
      repeatProperties?.sumNegative &&
      repeatProperties.sumNegative(row[repeatProperties.key][repeatColumnIndex])
    );
  };

  const rowIcon = props.rowIcon && props.rowIcon(row);

  const getEmptyRow = (className?: string, text?: string) => (
    <TableRow className={clsx(cssExports.tableRow, cssExports.empty, text ? cssExports.title : "", className)}>
      <TableCell className={clsx(cssExports.tableCell, cssExports.emptyCell, cssExports.first)}></TableCell>
      <TableCell
        className={clsx(cssExports.tableCell, repeatProperties ? cssExports.freezeCell : "", cssExports.second)}
        colSpan={2 + columns.length}
      >
        {text}
      </TableCell>
      {repeatPropertiesList && repeatProperties ? (
        [...Array(repeatColumnLength)].map((col, repeatIndex) => (
          <React.Fragment key={`repeat-columns-${repeatIndex}`}>
            {repeatPropertiesList.map(([repeatKey, cellValue], cellIndex) => (
              <TableCell
                key={`tab-repeatCell-${repeatIndex}${cellIndex}`}
                className={clsx(
                  cssExports.tableCell,
                  cellIndex === repeatPropertiesLength - 1 && repeatProperties?.showTotal ? cssExports.repeatEnd : "",
                  cellIndex === 0 ? cssExports.repeatTitleCell : cssExports.repeatCell,
                  cellValue?.isTextSmall ? cssExports.textSmall : "",
                  cellValue?.className,
                  cssExports.truncateString
                )}
                style={{
                  whiteSpace: cellValue?.noWrap ? "nowrap" : "normal",
                  width: cellValue?.width,
                  maxWidth: cellValue?.maxWidth,
                }}
              ></TableCell>
            ))}
          </React.Fragment>
        ))
      ) : (
        <></>
      )}
      {repeatProperties?.showTotal ? (
        <TableCell
          key={`tab-repeat-header-total`}
          className={clsx(cssExports.tableCell, cssExports.totalCell)}
        ></TableCell>
      ) : (
        <></>
      )}
      <TableCell className={clsx(cssExports.tableCell, cssExports.emptyCell, cssExports.last)}></TableCell>
    </TableRow>
  );

  const getTotalCellValue = () => {
    return [...Array(repeatColumnLength)].reduce(
      (prev: number, current: number, currentIndex: number) =>
        prev +
        (repeatProperties!.showTotal?.valueFormatter
          ? repeatProperties!.showTotal?.valueFormatter(
              row[repeatProperties!.key][currentIndex],
              index,
              depth,
              currentIndex,
              row
            )
          : row[repeatProperties!.key][currentIndex][repeatProperties!.showTotal!.key]),
      0
    );
  };

  const calculateIndentMarginLeft = (cellIndex: number, depth: number): number => {
    if (cellIndex !== 0) return 0;
    if (depth >= 3) return (depth - 2) * 3;
    return 0;
  };

  const calculateIndentPaddingLeft = (cellIndex: number, depth: number, hasChildren: boolean): number => {
    if (cellIndex !== 0) return 0;
    if (depth >= 3) {
      return hasChildren ? 1 : (depth - 2) * 3;
    }
    if (depth === 2) return 1;
    return 0;
  };

  const getRowFooter = () => {
    if (!rowFooterRenderer || depth !== 0 || !row.expanded) return null;

    const footerContent = rowFooterRenderer(row, index);
    if (!footerContent) return null;

    // Calculate total colspan for the footer row
    const totalColSpan =
      3 +
      columns.length +
      repeatColumnLength * (repeatPropertiesList?.length || 0) +
      (repeatProperties?.showTotal ? 1 : 0) +
      1;

    return (
      <TableRow
        className={clsx(
          cssExports.tableRow,
          cssExports.nestedTableSubRows,
          cssExports.nestedTableSubRowMain,
          cssExports.nestedTableRowFooter
        )}
      >
        <TableCell colSpan={totalColSpan} className={clsx(cssExports.tableCell)}>
          {footerContent}
        </TableCell>
      </TableRow>
    );
  };

  return (
    <>
      {[TableEnums.RowDisplayType.Empty, TableEnums.RowDisplayType.Title].every((t) => rowDisplayType !== t) ? (
        <>
          <TableRow
            hover
            className={clsx(
              cssExports.tableRow,
              getRowDisplayTypeClass(),
              depth > 0 ? cssExports.nestedTableSubRows : "",
              depth === 1 ? cssExports.nestedTableSubRowMain : "",
              depth > 1 ? cssExports.nestedTableSubRowsLowerTier : "",
              isImmediateChildHasDisplayType ? cssExports.noBorder : "",
              hasChildren || props.rowClickHandler ? cssExports.expandableRow : ""
            )}
            classes={{ hover: cssExports.tableRowHover }}
            style={{
              cursor: props.rowClickHandler ? "pointer" : "auto",
            }}
            onClick={onClickRow.bind(null, row, index, parentIndex)}
          >
            <TableCell
              key={`tab-headerTop`}
              className={clsx(
                cssExports.tableCell,
                cssExports.emptyCell,
                cssExports.first,
                allowCheckBoxSelection ? cssExports.select : ""
              )}
              onClick={(e: any) => {
                e.stopPropagation();
              }}
            >
              {depth === 0 && allowCheckBoxSelection ? (
                <CheckBox
                  checked={row.selected ?? false}
                  onClick={(e: any) => {
                    e.stopPropagation();
                  }}
                  className={row.disabled ? cssExports.disable : ""}
                  disabled={row.disabled}
                  onChange={toggleSelectionHandler.bind(null, index)}
                />
              ) : (
                <></>
              )}
            </TableCell>
            {depth === 1 ? (
              <TableCell className={clsx(cssExports.tableCell, cssExports.emptyCell, cssExports.second)}></TableCell>
            ) : depth > 0 ? (
              <>
                <TableCell className={clsx(cssExports.tableCell, cssExports.emptyCell, cssExports.second)} />
                <TableCell className={clsx(cssExports.tableCell, cssExports.emptyCell, cssExports.third)} />
              </>
            ) : (
              <></>
            )}
            {depth < 2 && (hasMultiLevels || (!hasMultiLevels && depth === 0)) ? (
              <TableCell
                className={clsx(
                  cssExports.tableCell,
                  hasChildren && rowDisplayType === undefined ? cssExports.expansionCell : "",
                  getColumnSumNegative(0) ? cssExports.sumNegative : "",
                  depth === 0 ? cssExports.second : cssExports.third
                )}
              >
                {hasChildren && rowDisplayType === undefined ? (
                  row.expanded ? (
                    <Icon icon={IconDefinitionConstants.WChevronSmallDown} />
                  ) : (
                    <Icon icon={IconDefinitionConstants.WChevronSmallRight} />
                  )
                ) : (
                  <></>
                )}
              </TableCell>
            ) : (
              <></>
            )}
            {depth === 0 && rowDisplayType === undefined && props.rowIcon ? (
              <TableCell className={clsx(cssExports.tableCell, cssExports.mainRowIconCell, cssExports.third)}>
                {rowIcon ? typeof rowIcon === "string" ? <img src={rowIcon} /> : <Icon icon={rowIcon} /> : <></>}
              </TableCell>
            ) : (
              <></>
            )}
            {columns.map(([key, cellValue], cellIndex) => {
              const isThirdColumn =
                cellIndex === 0 &&
                ((depth === 0 && (rowDisplayType !== undefined || !props.rowIcon)) || !hasMultiLevels);
              const shouldRenderInlineExpander =
                depth >= 2 && cellIndex === 0 && !!hasChildren && rowDisplayType === undefined;
              const inlineOffsetPx = shouldRenderInlineExpander
                ? calculateIndentMarginLeft(cellIndex, depth) * 8 + 16
                : 0;
              const contentMaxWidth = `calc(100% - ${inlineOffsetPx}px)`;
              return (
                <TableCell
                  key={`tab-cell-${index}${cellIndex}`}
                  className={clsx(
                    cssExports.tableCell,
                    cellIndex === columns.length - 1 && repeatProperties ? cssExports.freezeCell : "",
                    cellIndex === 0 ? cssExports.titleCell : "",
                    cellValue?.isTextSmall ? cssExports.textSmall : "",
                    getColumnSumNegative(0) ? cssExports.sumNegative : "",
                    isThirdColumn ? cssExports.third : cssExports.fourth,
                    cellValue?.className
                  )}
                  align={cellValue?.alignment}
                  style={{
                    width: cellValue?.width,
                    maxWidth: cellValue?.maxWidth,
                  }}
                  colSpan={isThirdColumn ? 2 : undefined}
                >
                  <Grid container wrap="nowrap" direction="row">
                    {shouldRenderInlineExpander && (
                      <Grid
                        item
                        className={cssExports.expansionCell}
                        justifyItems="center"
                        alignContent="center"
                        marginLeft={calculateIndentMarginLeft(cellIndex, depth)}
                      >
                        {row.expanded ? (
                          <Icon icon={IconDefinitionConstants.WChevronSmallDown} />
                        ) : (
                          <Icon icon={IconDefinitionConstants.WChevronSmallRight} />
                        )}
                      </Grid>
                    )}

                    <Grid
                      item
                      xs
                      paddingLeft={calculateIndentPaddingLeft(cellIndex, depth, !!hasChildren)}
                      style={{ maxWidth: contentMaxWidth }}
                    >
                      {getFormattedValue(
                        cellValue?.formatter ? cellValue.formatter(row, index, depth) : row[key]
                      )}
                    </Grid>
                  </Grid>
                </TableCell>
              );
            })}
            {repeatPropertiesList && repeatProperties ? (
              <>
                {[...Array(repeatColumnLength)].map((col, repeatIndex) => (
                  <React.Fragment key={`repeat-columns-${repeatIndex}`}>
                    {repeatPropertiesList.map(([repeatKey, cellValue], cellIndex) => (
                      <TableCell
                        key={`tab-repeatCell-${repeatIndex}${cellIndex}`}
                        className={clsx(
                          cssExports.tableCell,
                          cellIndex === repeatPropertiesLength - 1 && repeatProperties?.showTotal
                            ? cssExports.repeatEnd
                            : "",
                          cssExports.repeatCell,
                          cellValue?.isTextSmall ? cssExports.textSmall : "",
                          cellValue?.className,
                          cssExports.truncateString,
                          getColumnSumNegative(repeatIndex) ? cssExports.sumNegative : ""
                        )}
                        align={cellValue?.alignment}
                        style={{
                          width: cellValue?.width,
                          maxWidth: cellValue?.maxWidth,
                        }}
                      >
                        {getFormattedValue(
                          cellValue?.formatter
                            ? cellValue.formatter(
                                row[repeatProperties.key][repeatIndex],
                                index,
                                depth,
                                repeatIndex,
                                row
                              )
                            : row[repeatProperties.key][repeatIndex][repeatKey]
                        )}
                      </TableCell>
                    ))}
                  </React.Fragment>
                ))}
                {repeatProperties.showTotal ? (
                  <TableCell
                    key={`tab-repeatCell-total`}
                    className={clsx(
                      cssExports.tableCell,
                      cssExports.repeatTitleCell,
                      cssExports.truncateString,
                      cssExports.totalCell
                    )}
                    align="right"
                  >
                    {repeatProperties.showTotal?.displayFormatter
                      ? getFormattedValue(repeatProperties.showTotal.displayFormatter(getTotalCellValue()))
                      : getTotalCellValue()}
                  </TableCell>
                ) : (
                  <></>
                )}
              </>
            ) : (
              <></>
            )}
            <TableCell className={clsx(cssExports.tableCell, cssExports.emptyCell)}></TableCell>
          </TableRow>
          {hasChildren && row.expanded && rowDisplayType === undefined ? (
            <>
              {row[childrenKey].map((childRow: any, childRowIndex: number) => (
                <NestedTableRow
                  {...props}
                  key={`tab-row-${depth + 1}-${childRowIndex}`}
                  row={childRow}
                  index={childRowIndex}
                  parentIndex={index}
                  path={[...path, index]}
                  depth={depth + 1}
                  rowFooterRenderer={undefined} // Explicitly remove rowFooter for child rows
                />
              ))}
            </>
          ) : (
            <></>
          )}
          {getRowFooter()}
        </>
      ) : (
        getEmptyRow(undefined, row[columns[0][0]])
      )}
    </>
  );
};

export default NestedTableRow;
